import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Sparkles, TrendingUp, TrendingDown, Minus, Mic, Target, Award, AlertCircle, ChevronRight, RefreshCw, BarChart3, CreditCard, ExternalLink, Receipt } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" });
  } catch (e) { return iso; }
};

const TrendBadge = ({ delta }) => {
  if (delta === null || delta === undefined) {
    return <span className="text-xs text-novatus-text-3 inline-flex items-center gap-1"><Minus className="w-3 h-3" />Kein Trend</span>;
  }
  if (delta > 0) {
    return <span className="text-xs text-novatus-green font-bold inline-flex items-center gap-1" data-testid="profile-trend-up"><TrendingUp className="w-3 h-3" />+{delta} Punkte</span>;
  }
  if (delta < 0) {
    return <span className="text-xs text-red-400 font-bold inline-flex items-center gap-1" data-testid="profile-trend-down"><TrendingDown className="w-3 h-3" />{delta} Punkte</span>;
  }
  return <span className="text-xs text-novatus-text-2 inline-flex items-center gap-1"><Minus className="w-3 h-3" />Stabil</span>;
};

const ProfilePage = () => {
  const { userId } = useParams();
  const isAdminView = Boolean(userId);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [coachTips, setCoachTips] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);

  const endpoint = isAdminView ? `/admin/users/${userId}/profile` : "/me/profile";
  const backHref = isAdminView ? "/team" : "/dashboard";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(endpoint);
      setProfile(data);
      if (!isAdminView) {
        try {
          const tipsRes = await api.get("/me/coach-tips");
          setCoachTips(tipsRes.data || []);
        } catch (e) { /* tips are non-critical */ }
        try {
          const subRes = await api.get("/payments/my-subscription");
          setSubscription(subRes.data);
        } catch (e) { /* non-critical */ }
      }
    } catch (e) {
      toast.error("Profil konnte nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };

  const openBillingPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/payments/billing-portal", {
        return_url: window.location.href,
      });
      if (data?.url) window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Billing-Portal konnte nicht geöffnet werden");
    } finally {
      setPortalLoading(false);
    }
  };

  const dismissTip = async (id) => {
    try {
      await api.delete(`/me/coach-tips/${id}`);
      setCoachTips(coachTips.filter(t => t.id !== id));
      toast.success("Tipp entfernt");
    } catch (e) { toast.error("Fehler"); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId]);

  const regenerate = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/me/profile/regenerate");
      setProfile(data);
      toast.success("Profil aktualisiert");
    } catch (e) {
      toast.error("Aktualisierung fehlgeschlagen");
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) return <div className="p-12 text-center text-novatus-text-3"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;
  if (!profile) return <div className="p-12 text-center text-novatus-text-3">Kein Profil verfügbar.</div>;

  const agg = profile.aggregate || {};
  const nar = profile.narrative || {};
  const empty = agg.empty;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
      <Link to={backHref} className="text-sm text-novatus-text-3 hover:text-novatus-green inline-flex items-center gap-1 mb-4" data-testid="profile-back-link">
        <ArrowLeft className="w-4 h-4" /> Zurück
      </Link>

      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div className="animate-slide-up">
          <span className="label-eyebrow" data-testid="profile-eyebrow">
            {isAdminView && profile.viewed_user
              ? `Profil von ${profile.viewed_user.first_name} ${profile.viewed_user.last_name}`
              : "Dein persönliches Lern-Profil"}
          </span>
          <h1 className="font-display font-black text-3xl sm:text-5xl tracking-tight text-white mt-2">
            {nar.headline || "Dein Profil"}
          </h1>
          <p className="text-novatus-text-2 mt-3 max-w-2xl leading-relaxed">{nar.summary}</p>
        </div>
        {!isAdminView && (
          <button
            onClick={regenerate}
            disabled={refreshing}
            className="btn-ghost-green h-10 px-4 rounded-lg flex items-center gap-2 disabled:opacity-50"
            data-testid="profile-refresh-btn"
          >
            {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Neu berechnen
          </button>
        )}
      </div>

      {empty ? (
        <div className="nv-card p-10 text-center" data-testid="profile-empty">
          <Target className="w-10 h-10 text-novatus-text-3 mx-auto mb-4" />
          <p className="text-novatus-text-2">Schließe dein erstes Training ab — danach erscheint hier dein persönliches Profil mit Stärken, Schwächen und Empfehlungen.</p>
          <Link to="/training" className="btn-green h-10 px-5 rounded-lg inline-flex items-center gap-2 mt-5">
            Erstes Training starten <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      ) : (
        <>
          {/* Coach tips — only on own profile */}
          {!isAdminView && coachTips.length > 0 && (
            <div className="mb-6 animate-slide-up" data-testid="coach-tips-section">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-5 h-5 text-novatus-green" />
                <h2 className="font-display font-bold text-lg text-white">Mein Coach</h2>
                <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-novatus-green/15 text-novatus-green border border-novatus-green/40">
                  {coachTips.length} {coachTips.length === 1 ? "Tipp" : "Tipps"} für dich
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {coachTips.map(tip => (
                  <div key={tip.id} className="nv-card p-5 border-l-4 border-l-novatus-green" data-testid={`coach-tip-${tip.id}`}>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex-1 min-w-0">
                        <p className="label-eyebrow text-novatus-green">{tip.focus_area}</p>
                        <h3 className="font-display font-bold text-white mt-1">{tip.title}</h3>
                      </div>
                      <button
                        onClick={() => dismissTip(tip.id)}
                        className="p-1.5 rounded-md text-novatus-text-3 hover:text-white hover:bg-white/5 shrink-0"
                        title="Tipp entfernen"
                        data-testid={`dismiss-tip-${tip.id}`}
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="text-sm text-novatus-text-2 whitespace-pre-wrap leading-relaxed">{tip.content}</p>
                    {tip.approved_by_name && (
                      <p className="text-[10px] uppercase tracking-wider text-novatus-text-3 mt-3">
                        Von {tip.approved_by_name} · {new Date(tip.created_at).toLocaleDateString("de-DE")}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Active subscription card */}
          {!isAdminView && subscription?.active && subscription.subscription && (
            <div className="mb-6 nv-card p-5 border-l-4 border-l-novatus-green" data-testid="subscription-card">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-novatus-green/10 border border-novatus-green/40 flex items-center justify-center shrink-0">
                  <CreditCard className="w-5 h-5 text-novatus-green" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="label-eyebrow text-novatus-green">Aktives Abo</p>
                  <h3 className="font-display font-bold text-white text-lg mt-1">
                    {subscription.subscription.plan_label} · {subscription.subscription.minutes_per_month} Min/Monat
                  </h3>
                  <p className="text-xs text-novatus-text-3 mt-1">
                    Status: <span className="text-white font-bold">{subscription.subscription.status}</span>
                    {subscription.subscription.current_period_end && (
                      <> · Nächste Abrechnung: {new Date(subscription.subscription.current_period_end * 1000).toLocaleDateString("de-DE")}</>
                    )}
                    {subscription.subscription.cancel_at_period_end && (
                      <span className="ml-2 text-amber-300 font-bold">· Wird zum Periodenende beendet</span>
                    )}
                  </p>
                </div>
                <Link
                  to="/billing"
                  className="shrink-0 h-9 px-3 rounded-lg border border-novatus text-novatus-text-2 hover:bg-white/5 inline-flex items-center gap-1.5 text-xs font-bold"
                  data-testid="open-billing-portal"
                >
                  <Receipt className="w-3.5 h-3.5" />
                  Rechnungen & Abo
                </Link>
              </div>
            </div>
          )}

          {/* Stats row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6 animate-slide-up delay-1">
            <div className="nv-card p-4">
              <p className="label-eyebrow">Ø Score (30 Tage)</p>
              <p className="font-display font-black text-3xl text-novatus-green mt-1" data-testid="profile-score-30d">{agg.avg_score_30d ?? "—"}</p>
              <div className="mt-1"><TrendBadge delta={agg.trend_delta} /></div>
            </div>
            <div className="nv-card p-4">
              <p className="label-eyebrow">Analysiert</p>
              <p className="font-display font-black text-3xl text-white mt-1">{agg.sessions_analyzed || 0}</p>
              <p className="text-[10px] uppercase tracking-wider text-novatus-text-3 mt-1">Sessions</p>
            </div>
            <div className="nv-card p-4">
              <p className="label-eyebrow">Ø Tempo</p>
              <p className="font-display font-black text-3xl text-white mt-1">{agg.voice_avg?.wpm || "—"}</p>
              <p className="text-[10px] uppercase tracking-wider text-novatus-text-3 mt-1">{agg.voice_avg?.wpm_label || "WPM"}</p>
            </div>
            <div className="nv-card p-4">
              <p className="label-eyebrow">Szenarien</p>
              <p className="font-display font-black text-3xl text-white mt-1">{agg.scenario_coverage?.tried || 0}/{agg.scenario_coverage?.total || 0}</p>
              <p className="text-[10px] uppercase tracking-wider text-novatus-text-3 mt-1">getestet</p>
            </div>
          </div>

          {/* Strengths + Weaknesses */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
            <div className="nv-card p-6 animate-slide-up delay-2" data-testid="profile-strengths">
              <div className="flex items-center gap-2 mb-4">
                <Award className="w-5 h-5 text-novatus-green" />
                <h3 className="font-display font-bold text-white">Deine Stärken</h3>
              </div>
              <ul className="space-y-2.5">
                {(nar.key_strengths || []).length === 0 ? (
                  <li className="text-sm text-novatus-text-3">— Sammle erste Sessions —</li>
                ) : (nar.key_strengths || []).map((s, i) => (
                  <li key={`str-${i}`} className="flex items-start gap-2 text-sm text-novatus-text-2">
                    <span className="text-novatus-green mt-1.5 shrink-0">●</span>
                    <span className="text-white">{s}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="nv-card p-6 animate-slide-up delay-2" data-testid="profile-weaknesses">
              <div className="flex items-center gap-2 mb-4">
                <AlertCircle className="w-5 h-5 text-amber-400" />
                <h3 className="font-display font-bold text-white">Wo du wachsen kannst</h3>
              </div>
              <ul className="space-y-2.5">
                {(nar.key_weaknesses || []).length === 0 ? (
                  <li className="text-sm text-novatus-text-3">— Noch zu wenig Daten —</li>
                ) : (nar.key_weaknesses || []).map((w, i) => (
                  <li key={`weak-${i}`} className="flex items-start gap-2 text-sm">
                    <span className="text-amber-400 mt-1.5 shrink-0">●</span>
                    <span className="text-white">{w}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Weakest categories */}
          {agg.weakest_categories?.length > 0 && (
            <div className="nv-card p-6 mb-6 animate-slide-up delay-3" data-testid="profile-categories">
              <div className="flex items-center gap-2 mb-4">
                <BarChart3 className="w-5 h-5 text-novatus-green" />
                <h3 className="font-display font-bold text-white">Schwächste Kategorien</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {agg.weakest_categories.map((c) => (
                  <div key={c.name} className="bg-novatus-card-2 border border-novatus rounded-lg p-3">
                    <div className="font-display font-black text-2xl text-amber-400">{c.avg_score}</div>
                    <div className="text-[10px] uppercase tracking-wider text-novatus-text-3 mt-1 capitalize">{c.name.replace(/_/g, " ")}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Next learning topics */}
          {nar.next_topics?.length > 0 && (
            <div className="nv-card p-6 animate-slide-up delay-3" data-testid="profile-next-topics" style={{ borderColor: "rgba(31, 203, 79, 0.4)" }}>
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-5 h-5 text-novatus-green" />
                <h3 className="font-display font-bold text-white">Nächste Lernthemen</h3>
              </div>
              <p className="text-xs text-novatus-text-3 mb-4">Personalisiert von der KI basierend auf deinen letzten {agg.sessions_analyzed} Sessions.</p>
              <div className="space-y-3">
                {nar.next_topics.map((t, i) => (
                  <div
                    key={`topic-${i}`}
                    className="flex items-start gap-3 p-3 rounded-lg bg-novatus-card-2 border border-novatus hover:border-novatus-green/40 transition-colors"
                    data-testid={`profile-topic-${i}`}
                  >
                    <span className="text-novatus-green font-display font-black text-2xl shrink-0 w-8 text-center">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-white">{t.title}</p>
                      <p className="text-xs text-novatus-text-2 mt-1">{t.rationale}</p>
                    </div>
                    {t.scenario_id ? (
                      <Link
                        to={`/training`}
                        className="text-xs btn-green h-9 px-3 rounded-md inline-flex items-center gap-1 shrink-0"
                        data-testid={`profile-topic-scenario-${i}`}
                      >
                        Trainieren <ChevronRight className="w-3 h-3" />
                      </Link>
                    ) : (
                      <Link
                        to={`/training`}
                        className="text-xs btn-ghost-green h-9 px-3 rounded-md inline-flex items-center gap-1 shrink-0"
                      >
                        Üben <ChevronRight className="w-3 h-3" />
                      </Link>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Voice metrics summary */}
          {agg.voice_avg?.wpm > 0 && (
            <div className="nv-card p-6 mt-6 animate-slide-up delay-3" data-testid="profile-voice">
              <div className="flex items-center gap-2 mb-4">
                <Mic className="w-5 h-5 text-novatus-green" />
                <h3 className="font-display font-bold text-white">Voice-Verhalten (Ø)</h3>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-novatus-card-2 border border-novatus rounded-lg p-3 text-center">
                  <div className="font-display font-black text-2xl text-novatus-green">{agg.voice_avg.wpm}</div>
                  <div className="text-[10px] uppercase tracking-wider text-novatus-text-3 mt-1">WPM · {agg.voice_avg.wpm_label}</div>
                </div>
                <div className="bg-novatus-card-2 border border-novatus rounded-lg p-3 text-center">
                  <div className={`font-display font-black text-2xl ${agg.voice_avg.fillers > 3 ? "text-amber-400" : "text-white"}`}>{agg.voice_avg.fillers}</div>
                  <div className="text-[10px] uppercase tracking-wider text-novatus-text-3 mt-1">Füllwörter / Turn</div>
                </div>
                <div className="bg-novatus-card-2 border border-novatus rounded-lg p-3 text-center">
                  <div className="font-display font-black text-2xl text-white">{agg.voice_avg.long_pauses}</div>
                  <div className="text-[10px] uppercase tracking-wider text-novatus-text-3 mt-1">Lange Pausen / Turn</div>
                </div>
              </div>
            </div>
          )}

          <p className="text-xs text-novatus-text-3 mt-6 text-center">
            Profil zuletzt aktualisiert: {formatDate(profile.generated_at)} · Wird täglich neu berechnet.
          </p>
        </>
      )}
    </div>
  );
};

export default ProfilePage;
